import React from "react";
import { EuiText, EuiTitle } from "@elastic/eui";
import { AutonubilHomepageFooter } from "./footer";
import {
  Bug,
  Handshake,
  Target,
  CheckCircle2,
  Megaphone,
  Scale,
  RefreshCw,
  Mail,
} from "lucide-react";
import {
  SECURITY_CONTACT,
  SECURITY_POLICY,
  SecurityBlock,
  SecurityIcon,
  SecurityLang,
  SecuritySection,
} from "./security-policy";

/**
 * Public vulnerability-disclosure policy (CRA, work item opsanio/documentation#333).
 *
 * Deliberately a copy of the imprint's layout - same page shell, same eyebrow /
 * title / rule / lead header, same bordered card with the accent top bar, same
 * icon-and-heading sections, same homepage footer, and it sits under the same
 * `Home` route so it gets the same header. The two pages are the site's legal
 * pair; a researcher who follows security.txt here should land somewhere that
 * obviously belongs to the same company as the imprint.
 *
 * The copy comes from ./security-policy, not from translations/{de,en}.json,
 * because the work item pins one language per URL: /security is German and
 * /security/en is English regardless of the visitor's app language. That is what
 * `lang` selects - the chrome around it still follows the app language, so the
 * in-page language links below are the way between the two versions.
 */

interface SecurityProps {
  lang: SecurityLang;
}

const C = {
  accent: "var(--ak-accent)",
  accentLight: "var(--ak-accent-tint)",
  accentBorder: "var(--ak-accent-border)",
  pageBg: "var(--ak-page-bg)",
  cardBg: "var(--ak-surface)",
  border: "var(--ak-border)",
  textPrimary: "var(--ak-text)",
  textSecondary: "var(--ak-text-secondary)",
  textMuted: "var(--ak-text-muted)",
  sans: "system-ui,-apple-system,sans-serif",
  radius: 12,
};

const ICONS: Record<SecurityIcon, React.ReactNode> = {
  report: <Bug size={17} />,
  researchers: <Handshake size={17} />,
  scope: <Target size={17} />,
  commitment: <CheckCircle2 size={17} />,
  disclosure: <Megaphone size={17} />,
  safeHarbor: <Scale size={17} />,
  changes: <RefreshCw size={17} />,
};

/** Same shape as the imprint's ImprintSection - icon chip, heading, indented
 *  body, hairline separator - so the two pages read as one family. */
const SecuritySectionBlock: React.FC<{
  icon: React.ReactNode;
  title: string;
  last: boolean;
  children: React.ReactNode;
}> = ({ icon, title, last, children }) => (
  <div
    style={{
      paddingBottom: last ? 0 : 28,
      marginBottom: last ? 0 : 28,
      borderBottom: last ? undefined : `1px solid ${C.border}`,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: 8,
          background: C.accentLight,
          border: `1px solid ${C.accentBorder}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: C.accent,
        }}
      >
        {icon}
      </div>

      <EuiTitle size="xs">
        <h2 style={{ color: C.textPrimary, fontWeight: 700, margin: 0 }}>
          {title}
        </h2>
      </EuiTitle>
    </div>

    <EuiText color="subdued" size="s">
      <div style={{ lineHeight: 1.75, paddingLeft: 44 }}>{children}</div>
    </EuiText>
  </div>
);

const renderBlock = (block: SecurityBlock, key: number): JSX.Element =>
  "ul" in block ? (
    <ul key={key} style={{ margin: "0 0 12px", paddingLeft: 20 }}>
      {block.ul.map((item) => (
        <li key={item} style={{ marginBottom: 4 }}>
          {item}
        </li>
      ))}
    </ul>
  ) : (
    <p key={key} style={{ margin: "0 0 12px" }}>
      {block.p}
    </p>
  );

export const AutonubilSecurity: React.FC<SecurityProps> = ({ lang }) => {
  const policy = SECURITY_POLICY[lang];

  return (
    // lang is pinned to the policy, not to the app: the URL decides which
    // version this is, so screen readers and hyphenation must follow the URL
    // even when the surrounding chrome is in the visitor's other language.
    <div
      lang={policy.lang}
      style={{ background: C.pageBg, minHeight: "100vh", fontFamily: C.sans }}
    >
      <div
        style={{
          maxWidth: 800,
          margin: "0 auto",
          padding: "72px clamp(24px, 5vw, 48px) 80px",
        }}
      >
        <div style={{ marginBottom: 40 }}>
          <div
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: C.accent,
              marginBottom: 10,
            }}
          >
            {policy.eyebrow}
          </div>
          <EuiTitle size="l">
            <h1
              style={{
                color: C.textPrimary,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                marginBottom: 0,
              }}
            >
              {policy.title}
            </h1>
          </EuiTitle>
          <div
            style={{
              width: 56,
              height: 3,
              marginTop: 14,
              marginBottom: 16,
              background: `linear-gradient(90deg, ${C.accent} 0%, var(--ak-accent-2) 100%)`,
              borderRadius: 2,
            }}
          />
          <EuiText color="subdued">
            <p style={{ margin: 0, fontSize: "0.97rem", lineHeight: 1.75 }}>
              {policy.intro}
            </p>
          </EuiText>

          {/* Plain anchors, not react-router Links: the language versions are
              separate documents with their own URLs, and a full navigation is
              what makes them shareable and indexable as such. */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 16,
              marginTop: 18,
              fontSize: "0.82rem",
              color: C.textMuted,
            }}
          >
            <span>{policy.lastUpdatedLabel}</span>
            <span aria-hidden="true">·</span>
            <a
              href={policy.altLangPath}
              hrefLang={lang === "de" ? "en" : "de"}
              style={{ color: C.accent, fontWeight: 600 }}
            >
              {policy.altLangLabel}
            </a>
            <span aria-hidden="true">·</span>
            <a
              href="/.well-known/security.txt"
              style={{ color: C.accent, fontWeight: 600 }}
            >
              {policy.securityTxtLabel}
            </a>
          </div>
        </div>

        {/* The contact sits above the card, not buried in it: it is the one
            thing a reporter came for, and the policy is what they read after. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            background: C.accentLight,
            border: `1px solid ${C.accentBorder}`,
            borderRadius: C.radius,
            padding: "18px 22px",
            marginBottom: 24,
          }}
        >
          <div style={{ color: C.accent, flexShrink: 0 }}>
            <Mail size={20} />
          </div>
          <div>
            <div
              style={{
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: C.textMuted,
                marginBottom: 4,
              }}
            >
              {policy.contactLabel}
            </div>
            <a
              href={`mailto:${SECURITY_CONTACT}`}
              style={{
                color: C.accent,
                fontWeight: 700,
                fontSize: "1.05rem",
                textDecoration: "none",
              }}
            >
              {SECURITY_CONTACT}
            </a>
            <div
              style={{
                fontSize: "0.82rem",
                color: C.textSecondary,
                marginTop: 4,
              }}
            >
              {policy.contactHint}
            </div>
          </div>
        </div>

        <div
          style={{
            background: C.cardBg,
            border: `1px solid ${C.border}`,
            borderRadius: C.radius,
            boxShadow: "0 1px 4px var(--ak-shadow)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: 3,
              background: `linear-gradient(90deg, ${C.accent} 0%, var(--ak-accent-2) 100%)`,
            }}
          />

          <div style={{ padding: "clamp(24px, 4vw, 40px)" }}>
            {policy.sections.map((section: SecuritySection, index: number) => (
              <SecuritySectionBlock
                key={section.heading}
                icon={ICONS[section.icon]}
                title={section.heading}
                last={index === policy.sections.length - 1}
              >
                {section.blocks.map(renderBlock)}
              </SecuritySectionBlock>
            ))}
          </div>
        </div>

        {/* The imprint carries the legal block in its own sections; this page is
            reached directly from security.txt, so it repeats it rather than
            making a reporter go find out who they are talking to. */}
        <EuiText color="subdued" size="xs">
          <div style={{ marginTop: 28, lineHeight: 1.7, color: C.textMuted }}>
            {policy.legalBlock.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        </EuiText>
      </div>

      <AutonubilHomepageFooter />
    </div>
  );
};

export default AutonubilSecurity;
